/**
 * Lógica pura de la ficha de carga, de las acciones por línea y de la pestaña
 * de back orders. Sin React: todo lo que decide «qué se ve y qué se puede
 * pulsar» vive aquí para poder probarlo en el entorno `node` de vitest.
 *
 * El estado de cabecera lo calcula el backend (`core/cargas_estado.py`); aquí
 * NO se recalcula, sólo se interpreta para pintar el stepper y habilitar botones.
 * El backend sigue siendo el cierre real: un botón habilitado de más recibe un
 * 409 con código, y uno deshabilitado de más sólo es incomodidad.
 */
import type {
  AccionLinea, BackOrder, BotonesFicha, CargaSalida, CargaSalidaLinea, ErrorApi,
  EstadoBackOrder, FaseFicha, OrdenLineas, PasoFicha, SituacionPaso,
} from '@/types'

export type { BotonesFicha, ErrorApi, FaseFicha, OrdenLineas, PasoFicha, SituacionPaso } from '@/types'
import { reservaDe } from '@/utils/cargasSalida'
import { casaConHueco } from '@/utils/filtroHuecos'

// ── Recorrido de la ficha ────────────────────────────────────────────────────

export const FASES_FICHA: readonly FaseFicha[] = [
  'pedido', 'reserva', 'picking', 'paletizado', 'documentacion', 'expedicion',
]

/** Hay unidades que preparar y no están todas en picking. */
export function preparacionIncompleta(c: Pick<CargaSalida, 'qtySolicitada' | 'qtyPicking'>): boolean {
  return c.qtySolicitada > 0 && c.qtyPicking < c.qtySolicitada
}

/**
 * Las seis fases de la ficha, cada una con su situación SACADA DE SU DATO y no
 * del orden de los estados.
 *
 * Es lo que corrige el «reserva total en verde» de una carga en preparación
 * parcial: con el estado como índice, todo lo anterior al actual salía hecho
 * aunque la reserva estuviera a medias. Aquí cada fase mira lo suyo:
 *  - reserva: completa → hecho; algo → parcial; nada → pendiente. Con el
 *    picking entero ya no queda nada por reservar y cuenta hecho;
 *  - picking: entero → hecho; empezado → actual;
 *  - paletizado: palets reales con el picking entero (o carga sin palet) →
 *    hecho; reales a medias → actual;
 *  - documentación: hecho cuando hay alguna factura (`facturas`);
 *  - expedición: cargado → hecho; transporte marcado → actual.
 * Una carga ya cargada tiene todo hecho: salió, y lo que dijera el dato a
 * medias ya no es accionable.
 */
export function pasosFicha(c: CargaSalida, facturas = 0): PasoFicha[] {
  const cargado = c.estado === 'cargado' || c.expedida
  const pickingEntero = c.qtySolicitada > 0 && c.qtyPicking >= c.qtySolicitada
  const sit: Record<FaseFicha, SituacionPaso> = {
    pedido: 'hecho',
    reserva: (() => {
      if (c.qtySolicitada <= 0) return 'pendiente'
      if (pickingEntero) return 'hecho'
      const r = reservaDe(c).estado
      return r === 'completa' ? 'hecho' : r === 'parcial' ? 'parcial' : 'pendiente'
    })(),
    picking: pickingEntero ? 'hecho' : c.qtyPicking > 0 ? 'actual' : 'pendiente',
    paletizado: (pickingEntero && (c.paletsSonReales === true || c.cargaSinPalet === true))
      ? 'hecho'
      : c.paletsSonReales === true ? 'actual' : 'pendiente',
    documentacion: facturas > 0 ? 'hecho' : 'pendiente',
    expedicion: c.estado === 'transporte' || c.estado === 'transporte_confirmado' ? 'actual' : 'pendiente',
  }
  return FASES_FICHA.map(fase => ({ fase, situacion: cargado ? 'hecho' : sit[fase] }))
}

// ── Botones del estado manual y del aviso ────────────────────────────────────

/**
 * Qué botones de la ficha están habilitados para una carga.
 *
 * Desde 2026-11-03 la marca manual y el aviso valen en cualquier estado VIVO,
 * no sólo en «preparado»: el transporte se contrata mientras la carga aún se
 * prepara, y una carga parcial también espera camión (decidido con Carlos).
 *
 * - «Marcar transporte»: si no está ya en transporte simple (desde confirmado
 *   sirve para bajar a simple).
 * - «Confirmar fecha»: siempre que siga viva (también para cambiar la fecha).
 * - «Quitar»: sólo si hay marca; el backend admite `null` siempre.
 * - «Avisar»: siempre que siga viva.
 * Una carga expedida (`cargado`) no admite ninguna salvo quitar una marca.
 */
export function botonesFicha(c: Pick<CargaSalida, 'estado' | 'estadoManual'>): BotonesFicha {
  const viva = c.estado !== 'cargado'
  return {
    marcarTransporte: viva && c.estado !== 'transporte',
    confirmarTransporte: viva,
    quitarManual: c.estadoManual != null,
    avisarCliente: viva,
  }
}

/** La carga dice «transporte» pero aún le falta picking: se avisa en la fila y
 *  en la ficha, porque el estado ya no lo cuenta. */
export function transporteSinPreparar(
  c: Pick<CargaSalida, 'estado' | 'qtySolicitada' | 'qtyPicking'>,
): boolean {
  return (c.estado === 'transporte' || c.estado === 'transporte_confirmado') && preparacionIncompleta(c)
}

// ── Fechas con zona ──────────────────────────────────────────────────────────

const dos = (n: number) => String(Math.abs(n)).padStart(2, '0')

/**
 * Un `Date` como ISO 8601 CON el desfase local (`2026-11-02T10:30:00+01:00`).
 *
 * `toISOString()` daría la hora en UTC con `Z`, que es válida, pero el backend
 * guarda lo que recibe y quien lo lea en Dataverse vería una hora que no es la
 * que tecleó el usuario. Con el desfase, el instante es el mismo y la hora
 * escrita es la del operador. Sin zona el servidor la leería como UTC y
 * movería la hora dos.
 */
export function isoConDesfase(d: Date): string {
  const off = -d.getTimezoneOffset()
  const signo = off >= 0 ? '+' : '-'
  return (
    `${d.getFullYear()}-${dos(d.getMonth() + 1)}-${dos(d.getDate())}` +
    `T${dos(d.getHours())}:${dos(d.getMinutes())}:${dos(d.getSeconds())}` +
    `${signo}${dos(Math.floor(Math.abs(off) / 60))}:${dos(Math.abs(off) % 60)}`
  )
}

/** Valor de un `<input type="datetime-local">` (`yyyy-mm-ddThh:mm`, hora local)
 *  → ISO con desfase. `null` si está vacío o no es una fecha. */
export function datetimeLocalAIso(valor: string): string | null {
  if (!valor) return null
  const d = new Date(valor)
  return Number.isNaN(d.getTime()) ? null : isoConDesfase(d)
}

/** ISO (con zona) → valor para un `datetime-local`, en hora local. */
export function isoADatetimeLocal(iso?: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return `${d.getFullYear()}-${dos(d.getMonth() + 1)}-${dos(d.getDate())}T${dos(d.getHours())}:${dos(d.getMinutes())}`
}

// ── Acciones por línea ───────────────────────────────────────────────────────

export const ACCIONES_LINEA: readonly AccionLinea[] = ['parcial', 'esperar', 'eliminar']

/** La línea ya está servida entera: no hay nada que dejar en parcial ni esperar. */
export function lineaPreparada(l: Pick<CargaSalidaLinea, 'qtyPicking' | 'qtySolicitada'>): boolean {
  return l.qtySolicitada > 0 && l.qtyPicking >= l.qtySolicitada
}

/**
 * Qué acciones admite una línea.
 *
 * - Con back order FIRME (`estadoBo`) no se toca nada: el backend contesta 409
 *   `back_order_firme`, y ofrecerlo sólo sería provocar el error.
 * - Entera en picking: ni parcial ni esperar (409 `linea_ya_preparada`). Eliminar
 *   sí: sacar una línea de la carga no depende de cuánto se haya preparado.
 */
export function accionesPermitidas(l: CargaSalidaLinea): Record<AccionLinea, boolean> {
  if (l.estadoBo) return { parcial: false, esperar: false, eliminar: false }
  const preparada = lineaPreparada(l)
  return { parcial: !preparada, esperar: !preparada, eliminar: true }
}

export interface ChipBo { qty: number; estimado: boolean }

/** El chip «BO» de una línea: firme si tiene estado de BO, estimado si es sólo
 *  la acción «parcial» con lo que falta en vivo. `null` si no aplica. */
export function chipBoLinea(l: CargaSalidaLinea): ChipBo | null {
  if (l.estadoBo && l.qtyBackOrder != null) return { qty: l.qtyBackOrder, estimado: false }
  if (l.accion === 'parcial' && l.qtyBackOrder != null) return { qty: l.qtyBackOrder, estimado: true }
  return null
}

export const ORDENES_LINEAS: readonly OrdenLineas[] = ['defecto', 'reserva', 'picking']

/** Fracción servida de una línea. Sin unidades solicitadas cuenta como 1 (nada
 *  que servir): va al final del orden ascendente, no arriba como «sin avance». */
function fraccion(num: number, den: number): number {
  return den > 0 ? num / den : 1
}

/**
 * Líneas ordenadas por lo que se quiere ver primero. `defecto` respeta el
 * orden del backend. `reserva` y `picking` ponen arriba las MENOS avanzadas
 * (% ascendente): son las que hay que mirar para preparar la carga. Estable:
 * a igual porcentaje se conserva el orden de origen. No muta la entrada.
 */
export function ordenarLineas(lineas: readonly CargaSalidaLinea[], orden: OrdenLineas): CargaSalidaLinea[] {
  const copia = [...lineas]
  if (orden === 'defecto') return copia
  const clave = (l: CargaSalidaLinea) => orden === 'reserva'
    ? fraccion(l.qtyReservada, l.qtySolicitada)
    : fraccion(l.qtyPicking, l.qtySolicitada)
  return copia
    .map((l, i) => ({ l, i, k: clave(l) }))
    .sort((a, b) => a.k - b.k || a.i - b.i)
    .map(x => x.l)
}

// ── Back orders ──────────────────────────────────────────────────────────────

/** Días de calendario desde la fecha del BO hasta hoy. `null` si no hay fecha;
 *  nunca negativo (un reloj adelantado no debe dar «-1 días»). */
export function antiguedadDias(fechaBo?: string | null, hoy = new Date()): number | null {
  if (!fechaBo) return null
  const f = new Date(fechaBo)
  if (Number.isNaN(f.getTime())) return null
  // Por día de calendario local, no por 24 h: un BO de ayer a las 23:00 tiene
  // 1 día a las 08:00 de hoy, no 0.
  const a = new Date(f.getFullYear(), f.getMonth(), f.getDate()).getTime()
  const b = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate()).getTime()
  return Math.max(0, Math.round((b - a) / 86_400_000))
}

/** Los que aún piden atención: abiertos y previstos. */
function pendiente(b: BackOrder): boolean {
  return b.estadoBo === 'abierto' || b.estadoBo === 'previsto'
}

export function contarPendientes(bos: readonly BackOrder[]): number {
  return bos.filter(pendiente).length
}

/** Los filtros de columna de la pestaña. Listas vacías = no filtra; el rango
 *  va sobre el DÍA de `fechaBo` (de él sale la antigüedad), `''` = sin límite. */
export interface FiltrosBackOrders {
  cliente: string[]
  pedido: string[]
  articulo: string[]
  carga: string[]
  estado: EstadoBackOrder[]
  desde: string
  hasta: string
}

export type CampoFiltroBo = 'cliente' | 'pedido' | 'articulo' | 'carga' | 'estado' | 'fecha'

/** Arranca en abiertos y previstos: los cerrados se piden desde la columna. */
export const FILTROS_BO_INICIALES: FiltrosBackOrders = {
  cliente: [], pedido: [], articulo: [], carga: [], estado: ['abierto', 'previsto'], desde: '', hasta: '',
}

/** ¿Pasa el BO los filtros? `excepto` ignora uno de ellos: con eso cada
 *  columna saca su catálogo de las filas que pasan TODOS LOS DEMÁS (cascada),
 *  igual que la tabla de cargas. El cliente sin nombre casa con el hueco. */
export function pasaFiltroBackOrder(b: BackOrder, f: FiltrosBackOrders, excepto?: CampoFiltroBo): boolean {
  if (excepto !== 'cliente' && !casaConHueco(b.clienteNombre, f.cliente)) return false
  if (excepto !== 'pedido' && f.pedido.length > 0 && !f.pedido.includes(b.pedido)) return false
  if (excepto !== 'articulo' && f.articulo.length > 0 && !f.articulo.includes(b.articulo)) return false
  if (excepto !== 'carga' && f.carga.length > 0 && !f.carga.includes(b.carga)) return false
  if (excepto !== 'estado' && f.estado.length > 0 && !f.estado.includes(b.estadoBo)) return false
  if (excepto !== 'fecha' && (f.desde || f.hasta)) {
    const dia = b.fechaBo?.slice(0, 10)
    if (!dia) return false
    if (f.desde && dia < f.desde) return false
    if (f.hasta && dia > f.hasta) return false
  }
  return true
}

export function filtrarBackOrders(bos: readonly BackOrder[], f: FiltrosBackOrders): BackOrder[] {
  return bos.filter(b => pasaFiltroBackOrder(b, f))
}

/** Por cliente (alfabético, sin acentos ni mayúsculas) y, dentro, el más
 *  antiguo primero: el que más lleva esperando es el que hay que resolver. */
export function ordenarBackOrders(bos: readonly BackOrder[]): BackOrder[] {
  const nombre = (b: BackOrder) => b.clienteNombre ?? ''
  return [...bos].sort((a, b) =>
    nombre(a).localeCompare(nombre(b), undefined, { sensitivity: 'base' })
    || (a.fechaBo ?? '').localeCompare(b.fechaBo ?? ''))
}

// ── Errores del backend ──────────────────────────────────────────────────────

/** Un rechazo ya desenvuelto: **código, no texto**; la pantalla es bilingüe. */

export function errorApiDe(err: unknown): ErrorApi {
  const detalle = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
  if (detalle && typeof detalle === 'object' && 'codigo' in detalle
      && typeof (detalle as ErrorApi).codigo === 'string') {
    return detalle as ErrorApi
  }
  return { codigo: 'error_inesperado' }
}

// ── Cuándo sale ──────────────────────────────────────────────────────────────

export type SalidaRelativa =
  | { tipo: 'sinFecha' }
  | { tipo: 'hoy' }
  | { tipo: 'manana' }
  | { tipo: 'dias'; n: number }
  | { tipo: 'retraso'; n: number }

/**
 * El «SALE HOY» de la cabecera de la ficha: la fecha de salida prevista contra
 * hoy, por día de calendario. `fecha` es el día de Madrid ya convertido por el
 * backend (`fechaDe` lo lee como fecha local, sin hora).
 */
export function salidaRelativa(fecha: Date | null, hoy = new Date()): SalidaRelativa {
  if (!fecha) return { tipo: 'sinFecha' }
  const a = new Date(fecha.getFullYear(), fecha.getMonth(), fecha.getDate()).getTime()
  const b = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate()).getTime()
  const dias = Math.round((a - b) / 86_400_000)
  if (dias === 0) return { tipo: 'hoy' }
  if (dias === 1) return { tipo: 'manana' }
  return dias > 1 ? { tipo: 'dias', n: dias } : { tipo: 'retraso', n: -dias }
}
